import { isFuture, parseISO } from "date-fns";

import { AssessmentStatus } from "api/api-data-types";
import {
  AllAssessmentReportHeader,
  AllAssessmentTableRow,
  AssessmentCategory,
  OutcomeScoreReportHeader,
  PagePointReportHeader,
  SectionScoreBriefReportHeader,
  SectionScoreFullReportHeader,
  StudentAssessmentScoreTableRow,
} from "api/api-types-assessment";
import { relativeDayAndTimeFormat } from "i18n/helpers";
import { i18n, t } from "i18n/i18n";
import { AssessmentCategoryKey, AssessmentPublishStatusKey, ResourceKey } from "i18n/i18next";
import { compareDateAscNullsLast, compareDateDescNullsLast } from "utils/date";
import { CSVData } from "utils/file";
import { rollbarAndLogError } from "utils/logger";

/**
 * Returns the localized assessment status label based on completion state.
 * Used across assessment views to display consistent status text.
 *
 * Note: Callers should check for NOT_ASSESSABLE status before calling this function
 * if needed. HomeAssessmentList doesn't need this check (server pre-filters),
 * while SectionScoreDashboard and hooks.ts do.
 */
export const getAssessmentStatusLabel = (
  isMarkedComplete: boolean,
  assessedSubmissionsCount: number,
  isAssessmentComplete: boolean,
): string => {
  if (isMarkedComplete) {
    return assessedSubmissionsCount === 0 ? t("common.completed") : t("assessment_status.assessed");
  }

  return isAssessmentComplete ? t("assessment_status.assessed") : t("assessment_status.assessable");
};

export const formatAssessmentCategory = (category: AssessmentCategory): string =>
  t(`assessment_categories.${category as AssessmentCategoryKey}`);

export const mapStudentAssessmentScoreTableRow = (row: StudentAssessmentScoreTableRow) => ({
  id: row.page_id,
  hidden: row.hidden,
  title: row.title,
  topicTitle: row.topic_title,
  assessmentCategory: formatAssessmentCategory(row.assessment_category),
  points: row.points,
  outcomesCount: row.outcomes_count,
  outcomesTooltip: row.outcomes_tooltip,
  submittedAt: row.submitted_at || "-",
  assessmentComments: row.assessment_comments,
});

export type MappedStudentAssessmentScoreTableRow = ReturnType<
  typeof mapStudentAssessmentScoreTableRow
>;

export const mapAllAssessmentTableRow = (row: AllAssessmentTableRow) => ({
  id: row.page_id,
  hidden: row.hidden,
  title: row.title,
  topicTitle: row.topic_title ?? null,
  dueAtRaw: row.due_at,
  dueAtDate: row.due_at ? relativeDayAndTimeFormat(new Date(row.due_at)) : "-",
  submissionsLastCollectedAt: row.submissions_last_collected_at,
  status: row.status as AssessmentStatus,
  unassessedLateSubmissionsCount: row.unassessed_late_submissions_count,
  unseenLateSubmissionsCount: row.unseen_late_submissions_count,
  unseenSubmissionsCount: row.unseen_submissions_count,
  assessedSubmissionsCount: row.assessed_submissions_count,
  totalSubmissionsCount: row.total_submissions_count,
  isMarkedComplete: row.is_marked_complete,
  isAssessmentComplete: row.is_assessment_complete,
  publishStatus: (["published", "draft"].includes(row.publishing)
    ? row.publishing
    : "na") as AssessmentPublishStatusKey,
  assessedCount: row.assessed_count,
  assessmentCategory: formatAssessmentCategory(row.assessment_category),
  avgPoints: row.avg_points,
  outcomesCount: row.outcomes_count,
  outcomesTooltip: row.outcomes_tooltip,
  lastSubmittedDate: row.last_submitted_date
    ? t("common.date", { val: parseISO(row.last_submitted_date) })
    : "-",
  lastSubmittedRaw: row.last_submitted_date,
  lastAssessedDate: row.last_assessed_date
    ? t("common.date", { val: parseISO(row.last_assessed_date) })
    : "-",
  lastAssessedRaw: row.last_assessed_date,
  progress: row.progress,
});

export type MappedAllAssessmentTableRow = ReturnType<typeof mapAllAssessmentTableRow>;

export const isOpenForSubmissionRow = (
  row: Pick<
    MappedAllAssessmentTableRow,
    "totalSubmissionsCount" | "dueAtRaw" | "submissionsLastCollectedAt"
  >,
) =>
  row.totalSubmissionsCount === 0 &&
  Boolean(row.dueAtRaw && isFuture(row.dueAtRaw)) &&
  !row.submissionsLastCollectedAt;

export const partitionAllAssessmentDashboardRows = (
  allAssessmentsTableRows: MappedAllAssessmentTableRow[],
) => {
  const openForSubmissionsRows: MappedAllAssessmentTableRow[] = [];
  const assessableRows: MappedAllAssessmentTableRow[] = [];
  const assessedRows: MappedAllAssessmentTableRow[] = [];

  for (const row of allAssessmentsTableRows) {
    if (isOpenForSubmissionRow(row)) {
      openForSubmissionsRows.push({
        ...row,
        publishStatus: "na",
        avgPoints: "-",
        lastSubmittedDate: "-",
      });
    } else if (row.totalSubmissionsCount === 0) {
      // Do nothing
    } else if (
      row.status === AssessmentStatus.ASSESSABLE &&
      !row.isAssessmentComplete &&
      !row.isMarkedComplete
    ) {
      assessableRows.push(row);
    } else {
      assessedRows.push(row);
    }
  }

  assessableRows.sort((a, b) => compareDateDescNullsLast(a.lastSubmittedRaw, b.lastSubmittedRaw));
  assessedRows.sort((a, b) => compareDateDescNullsLast(a.lastAssessedRaw, b.lastAssessedRaw));
  openForSubmissionsRows.sort((a, b) => compareDateAscNullsLast(a.dueAtRaw, b.dueAtRaw));

  return { openForSubmissionsRows, assessableRows, assessedRows };
};

type OpenSubmissionColumn = {
  key: string;
  header: string;
  value: (row: MappedAllAssessmentTableRow) => string | number;
};

const OPEN_SUBMISSION_COLUMNS: OpenSubmissionColumn[] = [
  {
    key: "topic",
    header: t("assessment_dashboard.all.page_topic_header_title"),
    value: (row) => row.topicTitle || "-",
  },
  {
    key: "page",
    header: t("assessment_dashboard.all.page_header_title"),
    value: (row) => row.title,
  },
  { key: "status", header: t("assessment_dashboard.all.status_header_title"), value: () => "-" },
  {
    key: "publishing",
    header: t("assessment_dashboard.all.publishing_header_title"),
    value: () => "-",
  },
  {
    key: "submissions",
    header: t("assessment_dashboard.all.submissions_header_title"),
    value: () => "-",
  },
  {
    key: "progress",
    header: t("assessment_dashboard.all.progress_header_title"),
    value: () => "-",
  },
  {
    key: "assessment_category",
    header: t("assessment_dashboard.all.assessment_category_header_title"),
    value: (row) => row.assessmentCategory,
  },
  {
    key: "avg_points",
    header: t("assessment_dashboard.all.avg_points_header_title"),
    value: () => "-",
  },
  {
    key: "outcomes",
    header: t("assessment_dashboard.all.outcomes_header_title"),
    value: (row) => row.outcomesCount,
  },
  {
    key: "last_submitted",
    header: t("assessment_dashboard.all.last_submitted_header_title"),
    value: () => "-",
  },
  {
    key: "last_assessed",
    header: t("assessment_dashboard.all.last_assessed_header_title"),
    value: () => "-",
  },
  {
    key: "due_date",
    header: t("assessment_dashboard.all.due_date_header_title"),
    value: (row) => row.dueAtDate,
  },
];

export const buildOpenForSubmissionCSVData = (
  rows: MappedAllAssessmentTableRow[],
  hiddenColumns: Set<string> = new Set(),
) => {
  const visibleColumns = OPEN_SUBMISSION_COLUMNS.filter((col) => !hiddenColumns.has(col.key));
  const headers = visibleColumns.map((col) => col.header);
  const csvRows = rows.map((row) => visibleColumns.map((col) => col.value(row)));
  return { headers, rows: csvRows };
};

/**
 * Removes columns from CSV data whose header matches a value in the hidden set.
 * Call this BEFORE translation so headers are still symbolic keys.
 */
export const filterCSVColumns = (csvData: CSVData, hiddenColumns: Set<string>): CSVData => {
  if (hiddenColumns.size === 0) return csvData;

  const visibleIndices = csvData.headers
    .map((header, i) => (typeof header === "string" && hiddenColumns.has(header) ? -1 : i))
    .filter((i) => i !== -1);

  return {
    headers: visibleIndices.map((i) => csvData.headers[i]),
    rows: csvData.rows.map((row) => visibleIndices.map((i) => row[i])),
  };
};

/*
 *-------------------------------------------------------------------------------------
 * CSV Report Translation
 *
 * When the server generates CSV report data that contains text that must be localized
 * (e.g., table headers, textual cell data such as assessment category), it generates
 * a symbolic placeholder for it, which gets translated into the proper localized form
 * in one of the following functions.
 *-------------------------------------------------------------------------------------
 */

export const translateAllAssessmentReport = (csvData: CSVData): CSVData => {
  return translateReport({
    csvData,
    headerType: AllAssessmentReportHeader,
    translationGroup: "assessment_dashboard.all_assessment_report",
  });
};

export const translateOutcomeScoreReport = (csvData: CSVData): CSVData => {
  return translateReport({
    csvData,
    headerType: OutcomeScoreReportHeader,
    translationGroup: "assessment_dashboard.outcome_score_report",
  });
};

export const translatePagePointReport = (csvData: CSVData): CSVData => {
  return translateReport({
    csvData,
    headerType: PagePointReportHeader,
    translationGroup: "assessment_dashboard.page_point_report",
  });
};

export const translateSectionScoreBriefReport = (csvData: CSVData) => {
  return translateReport({
    csvData,
    headerType: SectionScoreBriefReportHeader,
    translationGroup: "assessment_dashboard.section_score_brief_report",
  });
};

export const translateSectionScoreFullReport = (csvData: CSVData) => {
  return translateReport({
    csvData,
    headerType: SectionScoreFullReportHeader,
    translationGroup: "assessment_dashboard.section_score_full_report",
  });
};

const translateReport = ({
  csvData,
  headerType,
  translationGroup,
}: {
  csvData: CSVData;
  headerType: Record<string, string>;
  translationGroup: string;
}): CSVData => {
  // Translate headers. If a header is one of the symbolic headers specified by the
  // `headerType` argument, replace it with the appropriate translation text. If no
  // translation can be found, report an error and let the raw value pass through.
  // If the header value is not a symbolic header (e.g., a student's name), pass it
  // through without translation.
  const symbolicHeaders = new Set(Object.values(headerType));
  const translatedHeaders = csvData.headers.map((header) => {
    if (typeof header === "string" && symbolicHeaders.has(header)) {
      const translationKey = `${translationGroup}.headers.${header}` as ResourceKey;
      if (i18n.exists(translationKey)) {
        return t(translationKey);
      }
      rollbarAndLogError("In translateCSVData: translation not found for header", { header });
    }
    return header;
  });

  // Translate rows. For each value in each row of the input data, if the value is in
  // one of the columns with a symbolic header, and if that symbolic header resolves to
  // a valid group of translations, then translate the value. Otherwise, pass it through
  // without translation.
  const translatedRows = csvData.rows.map((row) => {
    return row.map((value, idx) => {
      const header = csvData.headers[idx];
      if (typeof header === "string" && symbolicHeaders.has(header)) {
        const translationKey = `${translationGroup}.values.${header}.${value}` as ResourceKey;
        return i18n.exists(translationKey) ? t(translationKey) : value;
      }
      return value;
    });
  });

  return { headers: translatedHeaders, rows: translatedRows };
};
